import { useEffect, useRef, useState } from "react";
import { MessageSquare, X, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GREETING = {
  role: "assistant",
  text: "Welcome to Masters Pro Boxing. Ask me anything — eligibility, medical rules, matches, or how registration works.",
};

export default function AssistantChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);
  const sessionId = useRef(
    localStorage.getItem("mpb-assistant-session") ||
      (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()))
  );

  useEffect(() => {
    localStorage.setItem("mpb-assistant-session", sessionId.current);
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${API}/assistant`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: text, session_id: sessionId.current }),
      });
      if (!res.ok || !res.body) throw new Error("Assistant unavailable");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const payload = p.slice(6);
          if (payload === "[DONE]") continue;
          try {
            const { token } = JSON.parse(payload);
            if (token) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + token };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: "The assistant is catching its breath — please try again in a moment." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="assistant-toggle-button"
        aria-label="Open AI assistant"
        className="fixed bottom-5 right-5 z-[80] h-14 w-14 flex items-center justify-center bg-[#E10600] hover:bg-[#C00500] text-white shadow-[0_0_25px_rgba(225,6,0,0.45)] transition-colors duration-300"
      >
        {open ? <X className="h-6 w-6" /> : <MessageSquare className="h-6 w-6" />}
      </button>

      {open && (
        <div
          className="fixed bottom-24 right-5 z-[80] w-[calc(100vw-2.5rem)] max-w-sm border border-zinc-700 bg-[#0d0d0d] shadow-2xl flex flex-col"
          data-testid="assistant-panel"
        >
          <div className="border-b border-zinc-800 px-4 py-3 flex items-center gap-3">
            <img src="/images/mpb-shield.png" alt="" className="h-7 w-7 object-contain" />
            <div>
              <p className="font-cond font-bold uppercase tracking-widest text-white text-sm">MPB Cornerman</p>
              <p className="font-mono2 text-[9px] tracking-[0.25em] text-zinc-500 uppercase">AI Assistant · always in your corner</p>
            </div>
          </div>
          <div className="h-80 overflow-y-auto p-4 space-y-3" data-testid="assistant-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#E10600] text-white"
                      : "bg-[#1a1a1a] border border-zinc-800 text-zinc-200"
                  }`}
                  data-testid={`assistant-msg-${i}`}
                >
                  {m.text || (busy && i === messages.length - 1 ? "..." : "")}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
          <div className="border-t border-zinc-800 p-3 flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask the cornerman..."
              data-testid="assistant-input"
              className="flex-1 bg-[#111111] border border-zinc-700 focus:border-[#E10600] outline-none px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 transition-colors"
            />
            <button
              onClick={send}
              disabled={busy}
              data-testid="assistant-send-button"
              className="bg-[#E10600] hover:bg-[#C00500] disabled:opacity-50 text-white px-4 transition-colors"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
